import React, { useState, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
  ScrollView,
} from 'react-native';
import Text from '../../components/text';
import Feather from '@expo/vector-icons/Feather';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import TextInputComponent from '../../components/textinput/TextInputComponent';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import { showErrorToast, showSuccesToast } from '../tenants/tenantOnboarding/helpers';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';
import VerificationSection from '../../components/LandlordReadyness/VerificationSection';

const AccountEdit = ({ navigation }) => {
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const [state, setState] = useState({
    first_name: '',
    last_name: '',
    contact_number: '',
    email: '',
    address: '',
    postcode: '',
    id: '',
  });

  const [saving, setSaving] = useState(false);

  const _handleChange = (e) => {
    setState((prev) => ({ ...prev, [e.label]: e.value }));
  };

  useEffect(() => {
    ajax
      .get(Urls.PROFILE)
      .then(({ data }) => {
        if (data?.data?.userDetails) {
          const payload = data.data.userDetails;
          setState({
            first_name: payload?.first_name || '',
            last_name: payload?.last_name || '',
            contact_number: payload?.contact_number || '',
            email: payload?.email || '',
            address: payload?.address || '',
            postcode: payload?.postcode || '',
            id: payload?.id ? payload.id.toString() : '278',
          });
        }
      })
      .catch((error) => console.log('error get profile', error));
  }, []);

  const _onUpdate = () => {
    const { first_name, last_name, contact_number } = state;

    if (!first_name.trim()) {
      showErrorToast('First name is required.');
      return;
    }
    if (first_name.trim().length < 2) {
      showErrorToast('First name must be at least 2 characters long.');
      return;
    }

    if (!last_name.trim()) {
      showErrorToast('Last name is required.');
      return;
    }
    if (last_name.trim().length < 2) {
      showErrorToast('Last name must be at least 2 characters long.');
      return;
    }

    if (!contact_number.trim()) {
      showErrorToast('Contact number is required.');
      return;
    }
    if (!/^\d{8,}$/.test(contact_number.trim())) {
      showErrorToast('Contact number must contain at least 8 digits.');
      return;
    }

    setSaving(true);
    ajax
      .put(Urls.PROFILE, state)
      .then(({ data }) => {
        setSaving(false);
        if (data.status) {
          showSuccesToast('Profile updated successfully!');
          navigation.navigate('Settings');
        } else {
          showErrorToast('Something went wrong. Please try again.');
        }
      })
      .catch((error) => {
        setSaving(false);
        console.log('error update profile', error);
        showErrorToast('Failed to update profile.');
      });
  };

  return (
    <ScrollView style={[styles.container, isMobile && styles.containerMobile]} showsVerticalScrollIndicator={true}>
      <VerificationSection containerStyle={{ marginTop: 24, marginBottom: 40 }} />
      <Text style={styles.breadcrumbText}>Settings › My Account</Text>

      <View style={styles.headerRow}>
        <Text style={styles.pageTitle}>My Account</Text>
      </View>

      {/* CARD 1: EDIT PROFILE FORM matching Figma Node 448-151 / 450-100 */}
      <View style={[styles.cardContainer, isMobile && styles.cardContainerMobile]}>
        <Text style={styles.sectionTitle}>Edit Profile</Text>

        {/* Read-Only Hubita ID Field */}
        <View style={styles.fieldGroup}>
          <View style={styles.hubitaIdLabelRow}>
            <Text style={styles.fieldLabel}>Hubita ID</Text>
            <Text style={styles.readOnlyBadge}>Read only</Text>
          </View>
          <View style={styles.readOnlyInputBox}>
            <Text style={styles.readOnlyInputText}>{state.id || '278'}</Text>
          </View>
        </View>

        {/* 2-Column Grid Row 1: First Name & Last Name */}
        <View style={[styles.rowGrid, isMobile && styles.rowGridMobile]}>
          <View style={styles.colGrid}>
            <TextInputComponent
              title="First Name"
              placeholder="First name"
              value={state.first_name}
              onChangeText={(e) => _handleChange({ value: e, label: 'first_name' })}
              height={44}
            />
          </View>

          <View style={styles.colGrid}>
            <TextInputComponent
              title="Last Name"
              placeholder="Last name"
              value={state.last_name}
              onChangeText={(e) => _handleChange({ value: e, label: 'last_name' })}
              height={44}
            />
          </View>
        </View>

        {/* 2-Column Grid Row 2: Current Address & Postcode */}
        <View style={[styles.rowGrid, isMobile && styles.rowGridMobile]}>
          <View style={styles.colGrid}>
            <TextInputComponent
              title="Current Address"
              placeholder="Current address"
              value={state.address}
              onChangeText={(e) => _handleChange({ value: e, label: 'address' })}
              height={44}
            />
          </View>

          <View style={styles.colGrid}>
            <TextInputComponent
              title="Postcode"
              placeholder="Enter postcode"
              value={state.postcode}
              onChangeText={(e) => _handleChange({ value: e, label: 'postcode' })}
              height={44}
            />
          </View>
        </View>

        {/* 2-Column Grid Row 3: Contact Number & Email Address */}
        <View style={[styles.rowGrid, isMobile && styles.rowGridMobile]}>
          <View style={styles.colGrid}>
            <TextInputComponent
              title="Contact Number"
              placeholder="Contact number"
              value={state.contact_number}
              onChangeText={(e) => {
                const numericValue = e.replace(/[^0-9]/g, '');
                _handleChange({ value: numericValue, label: 'contact_number' });
              }}
              height={44}
              keyboardType="numeric"
              maxLength={15}
            />
          </View>

          <View style={styles.colGrid}>
            <TextInputComponent
              title="Email Address"
              placeholder="Email address"
              value={state.email}
              height={44}
              disabled={true}
            />
          </View>
        </View>

        {/* Form Action Footer Buttons */}
        <View style={styles.footerBtnRow}>
          <TouchableOpacity
            style={styles.cancelBtn}
            onPress={() => navigation.navigate('Settings')}
            activeOpacity={0.8}
          >
            <Text style={styles.cancelBtnText}>Cancel</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
            onPress={_onUpdate}
            disabled={saving}
            activeOpacity={0.85}
          >
            <Text style={styles.saveBtnText}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </ScrollView>
  );
};

export default drawerHoc({
  Component: AccountEdit,
  showBackButton: true,
  noScroll: true,
});

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  containerMobile: {
    padding: 14,
  },

  breadcrumbText: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 6,
  },

  headerRow: {
    marginBottom: 18,
  },

  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },

  /* MAIN CARD CONTAINER */
  cardContainer: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 24,
    ...SHADOWS.sm,
  },
  cardContainerMobile: {
    padding: 16,
    borderRadius: BORDER_RADIUS.md,
  },

  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: COLORS.textPrimary,
    marginBottom: 16,
  },

  sectionSubtitle: {
    fontSize: 13,
    color: COLORS.textSecondary,
    marginTop: 2,
  },

  /* HUBITA ID FIELD */
  fieldGroup: {
    marginBottom: 16,
  },
  hubitaIdLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
  },
  readOnlyBadge: {
    fontSize: 11,
    color: '#3B82F6',
    fontWeight: '500',
  },
  readOnlyInputBox: {
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    height: 44,
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  readOnlyInputText: {
    fontSize: 13,
    color: '#6B7280',
  },

  /* GRID */
  rowGrid: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  rowGridMobile: {
    flexDirection: 'column',
    gap: 12,
  },
  colGrid: {
    flex: 1,
  },

  /* FOOTER BUTTONS */
  footerBtnRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
    paddingTop: 16,
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
  },
  cancelBtn: {
    backgroundColor: '#F3F4F6',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 20,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
  },
  saveBtn: {
    backgroundColor: '#6C2BD9',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 24,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveBtnDisabled: {
    backgroundColor: '#9CA3AF',
  },
  saveBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FFFFFF',
  },

  /* VERIFIED LANDLORD SECTION */
  verifSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  verifSectionHeaderMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 12,
  },

  progressRightCol: {
    alignItems: 'flex-end',
    width: 140,
  },
  progressLabelText: {
    fontSize: 11,
    color: '#6B7280',
    marginBottom: 4,
    fontWeight: '500',
  },
  progressBarTrack: {
    height: 6,
    width: '100%',
    backgroundColor: '#F3F4F6',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#6C2BD9',
  },

  verifCardsRow: {
    flexDirection: 'row',
    gap: 16,
  },
  verifCardsRowMobile: {
    flexDirection: 'column',
    gap: 14,
  },

  verifSubCard: {
    flex: 1,
    backgroundColor: '#FAFAFA',
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    justifyContent: 'space-between',
  },
  verifSubCardMobile: {
    width: '100%',
  },

  verifHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  iconCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionBadge: {
    backgroundColor: '#FEF3C7',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  actionBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#D97706',
  },

  verifTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  verifDesc: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
    marginBottom: 16,
  },

  verifNowBtn: {
    backgroundColor: '#6C2BD9',
    borderRadius: BORDER_RADIUS.md,
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
  },
  verifNowBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});
